import { AddressDisplay, Button, Glass, Skeleton } from "@gotts.ai/ui";
import { useEffect, useState } from "react";

import { AnimatedBalance } from "../../components/AnimatedBalance";
import { CompositionBar } from "../../components/CompositionBar";
import { TokenIcon } from "../../components/TokenIcon";
import { useDevenvNavigate } from "../../hooks/useDevenvNavigate";
import { V3_NFT_POSITION_MANAGER_ABI } from "../../lib/abis";

import { decodeSqrtPriceX96, getDecimals } from "./types";

import type { UniswapDeployment } from "../../types";
import type { ReadContractFn, V3PoolState } from "./types";
import type { Address } from "viem";

// ---------------------------------------------------------------------------
// V3 Tick Position Bar
// ---------------------------------------------------------------------------

function TickPositionBar({
  tick,
  decodedPrice,
  tokenSymbols,
}: {
  tick: number;
  decodedPrice: string;
  tokenSymbols: readonly [string, string];
}) {
  const MIN_TICK = -887272;
  const MAX_TICK = 887272;
  const range = MAX_TICK - MIN_TICK;
  const position = ((tick - MIN_TICK) / range) * 100;
  const clampedPosition = Math.max(0, Math.min(100, position));

  return (
    <div data-testid="tick-position-bar" className="mt-3">
      <div className="mb-1.5 text-[11px] text-white/40">Tick Position</div>
      <div className="relative">
        <div
          className="absolute font-mono text-[9px] text-[#60a5fa]"
          style={{
            left: `${String(clampedPosition)}%`,
            transform: "translateX(-50%)",
            top: -14,
          }}
        >
          {decodedPrice} {tokenSymbols[1].toUpperCase()}/
          {tokenSymbols[0].toUpperCase()}
        </div>
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-white/[0.04]">
          {/* Gradient fill up to current position */}
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width: `${String(clampedPosition)}%`,
              background:
                "linear-gradient(90deg, rgba(96,165,250,0.15), rgba(96,165,250,0.3))",
            }}
          />
          <div
            className="absolute top-0 h-2 w-1.5 rounded-full bg-[#60a5fa]"
            style={{
              left: `${String(clampedPosition)}%`,
              transform: "translateX(-50%)",
            }}
          />
        </div>
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-white/25">
        <span>{MIN_TICK}</span>
        <span>{MAX_TICK}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// V3 Active Positions Table
// ---------------------------------------------------------------------------

function V3PositionsTable({
  deployment,
  readContract,
  feeTier,
  token0,
  token1,
}: {
  deployment: UniswapDeployment;
  readContract: ReadContractFn;
  feeTier: number;
  token0: Address;
  token1: Address;
}) {
  const [positions, setPositions] = useState<
    {
      tokenId: bigint;
      tickLower: number;
      tickUpper: number;
      liquidity: bigint;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const nftManager = deployment.v3.nonfungiblePositionManager;

  useEffect(() => {
    async function fetchPositions() {
      setLoading(true);
      try {
        const balance = await readContract(
          nftManager,
          V3_NFT_POSITION_MANAGER_ABI,
          "balanceOf",
          [deployment.deployer],
        );
        if (!balance) {
          setLoading(false);
          return;
        }

        const count = Math.min(Number(balance as bigint), 20);
        const found: typeof positions = [];

        for (let i = 0; i < count; i++) {
          const tokenId = await readContract(
            nftManager,
            V3_NFT_POSITION_MANAGER_ABI,
            "tokenOfOwnerByIndex",
            [deployment.deployer, BigInt(i)],
          );
          if (!tokenId) continue;

          const pos = await readContract(
            nftManager,
            V3_NFT_POSITION_MANAGER_ABI,
            "positions",
            [tokenId],
          );
          if (!pos) continue;

          const [
            ,
            ,
            posToken0,
            posToken1,
            posFee,
            tickLower,
            tickUpper,
            liquidity,
          ] = pos as [
            unknown,
            unknown,
            Address,
            Address,
            number,
            number,
            number,
            bigint,
            ...unknown[],
          ];

          // Filter to matching pool
          const [sorted0, sorted1] =
            token0.toLowerCase() < token1.toLowerCase()
              ? [token0, token1]
              : [token1, token0];
          if (
            posToken0.toLowerCase() === sorted0.toLowerCase() &&
            posToken1.toLowerCase() === sorted1.toLowerCase() &&
            posFee === feeTier
          ) {
            found.push({
              tokenId: tokenId as bigint,
              tickLower,
              tickUpper,
              liquidity,
            });
          }
        }
        setPositions(found);
      } catch {
        // ignore
      }
      setLoading(false);
    }
    void fetchPositions();
  }, [nftManager, deployment.deployer, readContract, feeTier, token0, token1]);

  if (loading)
    return (
      <div className="text-xs text-white/30" data-testid="positions-loading">
        Loading positions...
      </div>
    );
  if (positions.length === 0)
    return (
      <div className="text-xs text-white/30" data-testid="positions-empty">
        No active positions
      </div>
    );

  return (
    <div data-testid="v3-positions-table" className="mt-3">
      <div className="mb-1.5 text-[11px] font-medium text-white/50">
        Active Positions
      </div>
      <div className="space-y-1.5">
        {positions.map((p) => (
          <div
            key={p.tokenId.toString()}
            className="bg-surface flex items-center gap-2 rounded p-1.5 font-mono text-xs"
          >
            <span className="text-[#60a5fa]">#{p.tokenId.toString()}</span>
            <span className="text-white/50">
              [{p.tickLower}, {p.tickUpper}]
            </span>
            <span className="text-white/50">
              <AnimatedBalance value={Number(p.liquidity)} emDash={false} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// V3 Pool Card
// ---------------------------------------------------------------------------

export function V3PoolCard({
  pair,
  fee,
  feeTier,
  tokenAddresses,
  state,
  tokenSymbols,
  deployment,
  readContract,
}: {
  pair: string;
  fee: string;
  feeTier: number;
  tokenAddresses: [string, string] | null;
  state: V3PoolState | null;
  tokenSymbols: readonly [string, string];
  deployment: UniswapDeployment | null;
  readContract: ReadContractFn;
}) {
  const navigate = useDevenvNavigate();
  const [expanded, setExpanded] = useState(false);

  const decimals0 = getDecimals(tokenSymbols[0]);
  const decimals1 = getDecimals(tokenSymbols[1]);
  const needsInvert = tokenAddresses
    ? tokenAddresses[0].toLowerCase() > tokenAddresses[1].toLowerCase()
    : false;
  const sortedDecimals0 = needsInvert ? decimals1 : decimals0;
  const sortedDecimals1 = needsInvert ? decimals0 : decimals1;

  const decodedPrice = state
    ? decodeSqrtPriceX96(
        state.sqrtPriceX96,
        sortedDecimals0,
        sortedDecimals1,
        needsInvert,
      )
    : "N/A";
  const priceAnomaly = state
    ? Number(decodedPrice.replace(/,/g, "")) > 1e12
    : false;

  return (
    <Glass className="glass-card-interactive cursor-pointer p-3">
      {/* Header */}
      <button
        type="button"
        className="w-full text-left"
        onClick={() => {
          setExpanded((prev) => !prev);
        }}
        data-testid={`v3-pool-${pair}-${String(feeTier)}`}
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="flex -space-x-1.5">
              <TokenIcon symbol={tokenSymbols[0]} size={20} />
              <TokenIcon symbol={tokenSymbols[1]} size={20} />
            </div>
            <div>
              <span className="text-gold font-mono text-sm font-semibold">
                {pair}
              </span>
              <div className="flex items-center gap-1.5">
                <span className="pool-card-fee-pill">{fee}</span>
                <span className="font-mono text-[10px] text-white/25">
                  Concentrated
                </span>
              </div>
            </div>
          </div>
          <span className="text-text-muted text-xs">
            {expanded ? "Hide Details" : "View Details"}
          </span>
        </div>
      </button>

      {state ? (
        <div className="space-y-3">
          {/* Price */}
          <div className="pool-card-price-row">
            <span className="font-sans text-[11px] text-white/40">Price</span>
            <span className="font-mono text-[13px] text-white/80">
              {priceAnomaly ? (
                <span className="text-amber-400">Anomaly</span>
              ) : (
                <>
                  1 {tokenSymbols[0].toUpperCase()} = {decodedPrice}{" "}
                  {tokenSymbols[1].toUpperCase()}
                </>
              )}
            </span>
          </div>

          {/* Liquidity */}
          <div className="pool-card-price-row">
            <span className="font-sans text-[11px] text-white/40">
              Liquidity
            </span>
            <AnimatedBalance
              value={Number(state.liquidity)}
              className="font-mono text-[13px] text-white/70"
            />
          </div>

          <CompositionBar
            value0={50}
            value1={50}
            label0={tokenSymbols[0].toUpperCase()}
            label1={tokenSymbols[1].toUpperCase()}
            height={6}
          />
        </div>
      ) : (
        <div className="space-y-2">
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
        </div>
      )}

      {/* Expanded detail */}
      {expanded && (
        <div
          className="border-gold-dim mt-2 space-y-1 border-t pt-2"
          data-testid="v3-detail-panel"
        >
          {state ? (
            <>
              <div className="pool-card-price-row">
                <span className="text-[11px] text-white/40">Decoded Price</span>
                <span className="font-mono text-[12px] text-[#60a5fa]">
                  {decodedPrice} {tokenSymbols[1].toUpperCase()}/
                  {tokenSymbols[0].toUpperCase()}
                </span>
              </div>
              <div className="pool-card-price-row">
                <span className="text-[11px] text-white/40">Tick</span>
                <span className="font-mono text-[12px] text-white/60">
                  {state.tick}
                </span>
              </div>
              <details className="text-xs text-white/30">
                <summary className="cursor-pointer select-none text-[11px] text-white/30 hover:text-white/50">
                  Raw State
                </summary>
                <div className="mt-1 break-all font-mono text-[10px] text-white/20">
                  sqrtPriceX96: {state.sqrtPriceX96.toString()}
                </div>
              </details>
              <div className="flex items-center gap-1 text-[11px] text-white/40">
                <span>Pool:</span>
                <AddressDisplay
                  address={state.poolAddress}
                  fontSize={10}
                  copyable
                />
              </div>
              <TickPositionBar
                tick={state.tick}
                decodedPrice={decodedPrice}
                tokenSymbols={tokenSymbols}
              />
              {deployment && tokenAddresses && (
                <V3PositionsTable
                  deployment={deployment}
                  readContract={readContract}
                  feeTier={feeTier}
                  token0={tokenAddresses[0] as Address}
                  token1={tokenAddresses[1] as Address}
                />
              )}
            </>
          ) : (
            <div className="text-xs text-white/30">No on-chain data</div>
          )}
        </div>
      )}

      {/* Token addresses (collapsed) */}
      {!expanded && tokenAddresses && (
        <div className="mt-2 space-y-1">
          <div className="flex items-center gap-1 text-[11px] text-white/30">
            <span>A:</span>
            <AddressDisplay
              address={tokenAddresses[0]}
              fontSize={10}
              copyable
            />
          </div>
          <div className="flex items-center gap-1 text-[11px] text-white/30">
            <span>B:</span>
            <AddressDisplay
              address={tokenAddresses[1]}
              fontSize={10}
              copyable
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-2 flex gap-2 border-t border-[var(--glass-border)] pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `swap?tokenIn=${tokenSymbols[0]}&tokenOut=${tokenSymbols[1]}&version=v3`,
            );
          }}
        >
          {"\u21C4"} Swap
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `liquidity?token0=${tokenSymbols[0]}&token1=${tokenSymbols[1]}&version=v3`,
            );
          }}
        >
          + Add Liquidity
        </Button>
      </div>
    </Glass>
  );
}
